import { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "../ui/tooltip";
import { ShieldCheck, Info } from "lucide-react";

export function TwoFactorSection() {
  const { user, checkAuth, formatApiErrorDetail } = useAuth();
  const [step, setStep] = useState("idle"); // "idle" | "enrolling" | "disabling"
  const [qrCode, setQrCode] = useState(null);
  const [secret, setSecret] = useState(null);
  const [code, setCode] = useState("");
  const [disablePassword, setDisablePassword] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [busy, setBusy] = useState(false);

  const startEnroll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setQrCode(data.qr_code_data_url);
      setSecret(data.secret);
      setStep("enrolling");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const confirmEnroll = async () => {
    if (code.trim().length !== 6) return toast.error("Enter the 6-digit code from your authenticator app");
    setBusy(true);
    try {
      await api.post("/auth/2fa/enable", { code: code.trim() });
      await checkAuth();
      toast.success("Two-Factor enabled");
      setStep("idle");
      setCode("");
      setQrCode(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const confirmDisable = async () => {
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { password: disablePassword, code: disableCode.trim() });
      await checkAuth();
      toast.success("Two-Factor disabled");
      setStep("idle");
      setDisablePassword("");
      setDisableCode("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div id="settings-section-2fa" className="rounded-2xl bg-black/40 border border-white/10 p-6 space-y-4" data-testid="settings-2fa-section">
      <div className="flex items-center gap-2">
        <p className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#f1e0c5]/40"><ShieldCheck className="w-3.5 h-3.5" /> Two-Factor Login</p>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button data-testid="settings-2fa-info-button" className="text-[#f1e0c5]/30 hover:text-[#D4AF37]"><Info className="w-3.5 h-3.5" /></button>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">If you sell on the Bazaar, we recommend turning this on — it adds a second layer of protection for your account and earnings.</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {user?.two_factor_enabled && step !== "disabling" && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-[#4d7c0f]" data-testid="settings-2fa-enabled-status">Enabled — your account requires a code at login.</p>
          <Button data-testid="settings-2fa-disable-start-button" size="sm" variant="secondary" onClick={() => setStep("disabling")} className="rounded-full">Disable</Button>
        </div>
      )}

      {!user?.two_factor_enabled && step === "idle" && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-[#f1e0c5]/50">Off by default. Scan a QR code with an authenticator app to turn it on.</p>
          <Button data-testid="settings-2fa-enable-start-button" size="sm" onClick={startEnroll} disabled={busy} className="rounded-full bg-[#D4AF37] text-black hover:bg-[#c19b2e] shrink-0">Enable</Button>
        </div>
      )}

      {step === "enrolling" && (
        <div className="space-y-3" data-testid="settings-2fa-enroll-panel">
          <p className="text-sm text-[#f1e0c5]/60">Scan this with Google Authenticator, Authy, or similar:</p>
          {qrCode && <img src={qrCode} alt="Two-Factor QR code" data-testid="settings-2fa-qr-code" className="w-40 h-40 rounded-lg border border-white/10 bg-white p-2" />}
          <p className="text-xs text-[#f1e0c5]/40">Can't scan? Enter manually: <span className="font-mono text-[#D4AF37]" data-testid="settings-2fa-secret-text">{secret}</span></p>
          <Input
            data-testid="settings-2fa-confirm-code-input"
            inputMode="numeric"
            maxLength={6}
            placeholder="Enter the 6-digit code"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            className="bg-black/30 border-white/10"
          />
          <div className="flex gap-2">
            <Button data-testid="settings-2fa-confirm-enable-button" onClick={confirmEnroll} disabled={busy} className="rounded-full bg-[#D90429] hover:bg-[#b8031f]">Confirm & Enable</Button>
            <Button data-testid="settings-2fa-cancel-enroll-button" variant="ghost" onClick={() => { setStep("idle"); setQrCode(null); setCode(""); }} className="rounded-full">Cancel</Button>
          </div>
        </div>
      )}

      {step === "disabling" && (
        <div className="space-y-3" data-testid="settings-2fa-disable-panel">
          <p className="text-sm text-[#f1e0c5]/60">Confirm your password and a current code to disable Two-Factor.</p>
          <Input
            data-testid="settings-2fa-disable-password-input"
            type="password"
            placeholder="Current password"
            value={disablePassword}
            onChange={(e) => setDisablePassword(e.target.value)}
            className="bg-black/30 border-white/10"
          />
          <Input
            data-testid="settings-2fa-disable-code-input"
            inputMode="numeric"
            maxLength={6}
            placeholder="6-digit code"
            value={disableCode}
            onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            className="bg-black/30 border-white/10"
          />
          <div className="flex gap-2">
            <Button data-testid="settings-2fa-confirm-disable-button" onClick={confirmDisable} disabled={busy} variant="destructive" className="rounded-full">Disable Two-Factor</Button>
            <Button data-testid="settings-2fa-cancel-disable-button" variant="ghost" onClick={() => setStep("idle")} className="rounded-full">Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}
